/**
 * Circuit Breaker for API requests
 *
 * Implements a circuit breaker pattern to prevent thundering herd when backend recovers.
 * All API calls are queued when the circuit is OPEN and released with jittered delays
 * when the backend comes back online.
 *
 * States:
 * - CLOSED: Normal operation, requests pass through
 * - OPEN: Backend down, queue all requests and probe with exponential backoff
 */

import Axios from 'axios';
import { AXIOS_INSTANCE, dispatchVersionChange } from './axiosInstance';
import { tokenManager } from '../utils/tokenManager';
import { isPublicEndpoint } from './security-config';
import { InternalEventSource } from './InternalEventSource';
import { APP_VERSION } from '../version';

export type CircuitState = 'CLOSED' | 'OPEN';

/**
 * SSE Connection Configuration
 * Circuit breaker manages the EventSource connection and delivers events/errors via callbacks
 */
export interface SSEConnectionConfig {
  url: string;
  onOpen?: () => void;
  onEvent: (eventType: string, data: string) => void;
  onError: (error: Error) => void;
}

interface QueuedRequest {
  id: number;
  type: 'http' | 'sse' | 'sse-connection' | 'token-refresh';
  config?: any; // For HTTP requests
  execute?: (token: string) => Promise<void>; // For legacy SSE reconnects
  sseConfig?: SSEConnectionConfig; // For SSE connection requests
  eventSource?: EventSource; // Active EventSource for SSE connections
  resolve: (value: any) => void;
  reject: (error: any) => void;
  timestamp: number;
  needsTokenRefresh?: boolean; // Flag to refresh token before executing
  tokenRefreshAttempted?: boolean; // True if we've already tried refreshing for this request
}

interface CircuitBreakerConfig {
  maxRetries: number;
  baseDelay: number;
  maxDelay: number;
  jitterFactor: number;
  releaseJitterMax: number;
  failureThreshold: number;
  successThreshold: number;
}

interface CircuitBreakerDebugConfig {
  enabled: boolean;
  delayRange: {
    min: number;  // milliseconds
    max: number;  // milliseconds
  };
  verbose: boolean;  // log each delayed request
  affectsHTTP: boolean;
  affectsSSE: boolean;
  affectsTokenRefresh: boolean;
}

const DEFAULT_CONFIG: CircuitBreakerConfig = {
  maxRetries: Infinity,        // Never give up
  baseDelay: 1000,             // 1 second
  maxDelay: 30000,             // 30 seconds
  jitterFactor: 0.2,           // ±20%
  releaseJitterMax: 2000,      // 0-2s queue release jitter
  failureThreshold: 1,         // Open after 1 failure
  successThreshold: 1,         // Close after 1 success
};

type CircuitStateListener = (state: CircuitState, retryCount: number, nextRetryTime?: number) => void;

class CircuitBreaker {
  private static nextRequestId: number = 1;
  private state: CircuitState = 'CLOSED';
  private queue: QueuedRequest[] = [];
  private retryCount: number = 0;
  private retryTimeout: NodeJS.Timeout | null = null;
  private nextRetryTime: number | null = null;
  private config: CircuitBreakerConfig;
  private listeners: CircuitStateListener[] = [];
  private consecutiveFailures: number = 0;
  private consecutiveSuccesses: number = 0;
  private requestInFlight: boolean = false; // Track if a request is currently executing
  public debug: boolean = false; // Set to true from console to enable verbose logging
  public debugConfig: CircuitBreakerDebugConfig = {
    enabled: false,
    delayRange: { min: 1000, max: 3000 },
    verbose: true,
    affectsHTTP: true,
    affectsSSE: true,
    affectsTokenRefresh: false,
  };

  constructor(config: Partial<CircuitBreakerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Subscribe to circuit state changes
   */
  public onStateChange(listener: CircuitStateListener): () => void {
    this.listeners.push(listener);
    // Return unsubscribe function
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  /**
   * Get current circuit state
   */
  public getState(): CircuitState {
    return this.state;
  }

  /**
   * Get current retry count
   */
  public getRetryCount(): number {
    return this.retryCount;
  }

  /**
   * Get queue size
   */
  public getQueueSize(): number {
    return this.queue.length;
  }

  /**
   * Get timestamp of next retry attempt (null if circuit is CLOSED)
   */
  public getNextRetryTime(): number | null {
    return this.nextRetryTime;
  }

  /**
   * Apply a random delay if debug mode is enabled
   * Returns a promise that resolves after the delay
   */
  private async applyDebugDelay(request: QueuedRequest): Promise<void> {
    if (!this.debugConfig.enabled) {
      return;
    }

    // Check if this request type should be delayed
    const shouldDelay =
      (request.type === 'http' && this.debugConfig.affectsHTTP) ||
      (request.type === 'sse' && this.debugConfig.affectsSSE) ||
      (request.type === 'token-refresh' && this.debugConfig.affectsTokenRefresh);

    if (!shouldDelay) {
      return;
    }

    // Calculate random delay within range
    const { min, max } = this.debugConfig.delayRange;
    const delay = min + Math.round(Math.random() * (max - min));

    if (this.debugConfig.verbose) {
      const url = request.config?.url ?? request.sseConfig?.url ?? 'unknown';
      console.log(`[CircuitBreaker Debug] Request #${request.id}: Applying ${delay}ms delay to ${request.type} request to ${url}`, new Error().stack);
      console.log(request)
    }

    return new Promise(resolve => setTimeout(resolve, delay))
  }

  /**
   * Manually trigger an immediate retry (as if backoff timer expired)
   * Does not reset backoff progression
   */
  public retryNow(): void {
    if (this.state !== 'OPEN') {
      // Only allow manual retry when circuit is OPEN
      return;
    }

    // Clear scheduled probe
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    // Execute probe immediately
    this.nextRetryTime = null;
    this.executeProbe();
  }

  /**
   * Queue a request - only queues, doesn't execute
   */
  public queueRequest(config: any, options?: { needsTokenRefresh?: boolean }): Promise<any> {
    return new Promise((resolve, reject) => {
      // Push to tail
      this.queue.push({
        id: CircuitBreaker.nextRequestId++,
        type: 'http',
        config,
        resolve,
        reject,
        timestamp: Date.now(),
        needsTokenRefresh: options?.needsTokenRefresh,
      });

      // Wake up the processor
      this.processQueue();
    });
  }

  /**
   * Queue an SSE reconnect operation - only queues, doesn't execute
   * The reconnectFn receives the current token as a parameter when executed
   * @deprecated Use queueSSEConnection() instead for better separation of concerns
   */
  public queueSSEReconnect(reconnectFn: (token: string) => Promise<void>, needsTokenRefresh: boolean = false): Promise<void> {
    return new Promise((resolve, reject) => {
      // Push to tail
      this.queue.push({
        id: CircuitBreaker.nextRequestId++,
        type: 'sse' as const,
        execute: reconnectFn,
        resolve,
        reject,
        timestamp: Date.now(),
        needsTokenRefresh,
      });

      // Wake up the processor
      this.processQueue();
    });
  }

  /**
   * Queue an SSE connection request
   * Circuit breaker manages the EventSource and delivers events/errors via callbacks
   */
  public queueSSEConnection(config: SSEConnectionConfig): Promise<void> {
    return new Promise((resolve, reject) => {
      // Push to tail
      this.queue.push({
        id: CircuitBreaker.nextRequestId++,
        type: 'sse-connection' as const,
        sseConfig: config,
        resolve,
        reject,
        timestamp: Date.now(),
      });

      // Wake up the processor
      this.processQueue();
    });
  }

  /**
   * Queue a token refresh request
   * Triggers token refresh without making an API call
   */
  public queueTokenRefresh(): Promise<void> {
    return new Promise((resolve, reject) => {
      // Push to tail
      this.queue.push({
        id: CircuitBreaker.nextRequestId++,
        type: 'token-refresh' as const,
        resolve,
        reject,
        timestamp: Date.now(),
      });

      // Wake up the processor
      this.processQueue();
    });
  }

  /**
   * Process an SSE request from the queue (legacy method)
   */
  private async processSSERequest(request: QueuedRequest): Promise<void> {
      if(this.debug) {
        console.log('[CircuitBreaker] processSSERequest() - start');
      }
      // Get current token and pass to SSE reconnect function
      const token = tokenManager.getToken();
      if (!token) {
        // No token available - just reject this request
        // Don't call handleAuthError() - being on login page without token is normal
        if(this.debug) {
          console.log('[CircuitBreaker] No token available for SSE connection, rejecting request');
        }
        this.requestInFlight = false;
        this.queue.shift(); // Dequeue this request
        request.reject(new Error('No authentication token available'));
        this.processQueue(); // Process next request
        return;
      }

      if(this.debug) {
        console.log('[CircuitBreaker] processSSERequest() - calling request.execute() with token');
      }
      // Execute SSE reconnect with token
      // Don't dequeue - onopen will call reportSuccess() which dequeues
      // onerror will call reportFailure() which leaves in queue
      try {
        await request.execute!(token);
        if(this.debug) {
          console.log('[CircuitBreaker] processSSERequest() - request.execute() completed (no sync error)');
        }
        // Execute completed - but still don't dequeue
        // onopen/onerror handlers will manage dequeueing via reportSuccess/reportFailure
      } catch (err) {
        // Synchronous failure - request execution won't report, so we do it here
        if(this.debug) {
          console.log('[CircuitBreaker] processSSERequest() - request.execute() threw sync error:', err);
        }
        this.reportFailure(err);
      }
  }

  /**
   * Process an SSE connection request from the queue
   * Creates InternalEventSource with state machine and wires up callbacks
   */
  private async processSSEConnectionRequest(request: QueuedRequest): Promise<void> {
    if(this.debug) {
      console.log('[CircuitBreaker] processSSEConnectionRequest() - start');
    }

    const token = tokenManager.getToken();
    if (!token) {
      if(this.debug) {
        console.log('[CircuitBreaker] No token available for SSE connection, rejecting request');
      }
      this.requestInFlight = false;
      this.queue.shift(); // Dequeue this request
      request.reject(new Error('No authentication token available'));
      this.processQueue(); // Process next request
      return;
    }

    const config = request.sseConfig!;

    // Add token to URL as query parameter
    const url = `${config.url}?token=${encodeURIComponent(token)}`;

    if(this.debug) {
      console.log('[CircuitBreaker] Creating InternalEventSource:', url);
    }

    try {
      new InternalEventSource(
        url,
        // reportSuccess callback - connection is stable (first message received)
        async () => {
          if(this.debug) {
            console.log('[CircuitBreaker] InternalEventSource reportSuccess callback');
          }

          this.reportSuccess();

          // Call user's onOpen callback if provided
          if (config.onOpen) {
            try {
              config.onOpen();
            } catch (err) {
              console.error('[CircuitBreaker] Error in onOpen callback:', err);
            }
          }
        },
        // reportFailure callback - soft failure (timer expired or error during stabilization)
        () => {
          if(this.debug) {
            console.log('[CircuitBreaker] InternalEventSource reportFailure callback');
          }
          this.reportFailure(new Error('SSE connection failed'));
        },
        // messageHandler callback - deliver messages to upper layer
        (eventType: string, data: string) => {
          if(this.debug) {
            console.log(`[CircuitBreaker] InternalEventSource message: ${eventType}`);
          }
          try {
            config.onEvent(eventType, data);
          } catch (err) {
            console.error(`[CircuitBreaker] Error in onEvent callback for ${eventType}:`, err);
          }
        },
        // errorHandler callback - hard failure (error after connected)
        // Note: reportSuccess() already dequeued request, so just deliver error
        (error: Error) => {
          if(this.debug) {
            console.log('[CircuitBreaker] InternalEventSource errorHandler callback');
          }

          // Deliver error to upper layer (which will re-queue a new connection)
          try {
            config.onError(error);
          } catch (err) {
            console.error('[CircuitBreaker] Error in onError callback:', err);
          }
        }
      );

      // InternalEventSource now manages the connection lifecycle
      // reportSuccess() will be called when first message received (dequeues request)
      // reportFailure() will be called for soft failures (keeps in queue for probing)
      // errorHandler() will be called for hard failures (dequeues, upper layer will re-queue)

    } catch (err) {
      // Synchronous failure creating InternalEventSource
      if(this.debug) {
        console.log('[CircuitBreaker] Failed to create InternalEventSource:', err);
      }
      this.reportFailure(err);
    }
  }

  /**
   * Process a token refresh request from the queue
   * This type of request only refreshes the token without making an API call
   */
  private async processTokenRefreshRequest(request: QueuedRequest): Promise<void> {
    if (this.debug) {
      console.log('[CircuitBreaker] Processing token refresh request');
    }

    // Call the token refresh helper
    const refreshed = await this.processTokenRefresh(request);

    if (refreshed) {
      // Success - dequeue and resolve
      this.reportSuccess();

      request.resolve(undefined);
    }
    // If refresh failed, reportFailure was already called by processTokenRefresh
    // and request stays in queue for retry
  }

  /**
   * Refresh the access token before executing a request
   * Returns true if refresh succeeded, false otherwise
   */
  private async processTokenRefresh(request: QueuedRequest): Promise<boolean> {
    if (this.debug) {
      console.log('[CircuitBreaker] Processing token refresh before executing request');
    }

    // Check if refresh token exists before attempting refresh
    if (!tokenManager.getRefreshToken()) {
      if (this.debug) {
        console.log('[CircuitBreaker] No refresh token available - authentication error');
      }
      tokenManager.handleAuthError();
      this.rejectAllQueued(new Error('Authentication failed'));
      return false;
    }

    try {
      await tokenManager.refreshToken();
      request.needsTokenRefresh = false; // Clear flag on success
      if (this.debug) {
        console.log('[CircuitBreaker] Token refresh succeeded');
      }
      return true;
    } catch (error: any) {
      const status = error?.response?.status;

      // 401/403 = refresh token expired, go to login
      if (status === 401 || status === 403) {
        if(this.debug) {
          console.log('[CircuitBreaker] Token refresh failed - authentication error:', status);
        }
        tokenManager.handleAuthError();
        this.rejectAllQueued(new Error('Authentication failed'));
        return false;
      }

      // 5xx or network error = backend still down, schedule retry, keep flag
      if (this.debug) {
        console.log('[CircuitBreaker] Token refresh failed - backend still down, will retry');
      }
      this.reportFailure(error);
      return false; // Don't process original request
    }
  }

  /**
   * Process an HTTP request from the queue
   */
  private async processHTTPRequest(request: QueuedRequest): Promise<void> {
    // Check if this endpoint requires authentication
    const method = request.config.method?.toUpperCase() || 'GET';
    const url = request.config.url || '';
    const requiresAuth = !isPublicEndpoint(url, method);

    // Get current token and add to request if authentication required
    const token = requiresAuth ? tokenManager.getToken() : null;

    // Make axios call directly with bypass flag and token (if needed)
    const config = {
      ...request.config,
      __circuitBreakerBypass: true,
      headers: {
        ...request.config?.headers,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };

    try {
      const response = await AXIOS_INSTANCE(config);

      // Success - report to circuit breaker
      this.reportSuccess();

      // Resolve caller's promise with response data
      request.resolve(response.data);
    } catch (error: any) {
      // Check if this was canceled
      if (Axios.isCancel(error) || error.message === 'canceled') {
        this.reportCanceled();
        request.reject(error);
        return;
      }

      const status = error?.response?.status;

      // 401 = Unauthenticated (token missing, invalid, or expired)
      if (status === 401) {
        if (requiresAuth) {
          // Check if we've already refreshed the token for this request
          if (request.tokenRefreshAttempted) {
            // Token refresh already tried, still getting 401 = permission issue, not token issue
            if (this.debug) {
              console.log('[CircuitBreaker] 401 after token refresh - treating as forbidden, rejecting request');
            }
            this.reportSuccess(); // Backend is up
            request.reject(error);
            return;
          }

          // First 401 - try token refresh
          if (this.debug) {
            console.log('[CircuitBreaker] 401 error on authenticated endpoint - attempting token refresh');
          }
          request.needsTokenRefresh = true;
          request.tokenRefreshAttempted = true; // Mark that we're trying refresh
          this.requestInFlight = false;
          this.processQueue(); // Retry with refresh flag set
          return;
        } else {
          // Public endpoint 401: bad credentials, just reject
          if (this.debug) {
            console.log('[CircuitBreaker] 401 error on public endpoint - rejecting request');
          }
          this.reportSuccess(); // Backend is up, just bad credentials
          request.reject(error);
          return;
        }
      }

      // 403 = Forbidden (authenticated but not authorized)
      if (status === 403) {
        if(this.debug) {
          console.log('[CircuitBreaker] 403 error - forbidden');
        }
        this.reportSuccess(); // Backend is up, just forbidden
        request.reject(error);
        return;
      }

      // 426 = Upgrade Required (version mismatch) - don't retry
      if (status === 426) {
        if (this.debug) {
          console.log('[CircuitBreaker] 426 Upgrade Required - backend version mismatch, rejecting request');
        }

        // Report success (backend is up and working, just different version)
        this.reportSuccess();

        // Dispatch version-changed event so UI can show reload prompt
        const serverVersion = error?.response?.headers?.['x-app-version'];
        if (serverVersion) {
          dispatchVersionChange(APP_VERSION, serverVersion);
        }

        // Reject the request (don't retry)
        request.reject(error);
        return;
      }

      // Other 4xx or 501 (Not Implemented) = backend is up, don't retry
      if (status && (status >= 400 && status < 500 || status === 501)) {
        this.reportSuccess();
        request.reject(error);
        return;
      }

      // 5xx or network error = backend issue, retry
      this.reportFailure(error);
    }
  }

  /**
   * Process the queue - main event loop
   */
  private async processQueue(): Promise<void> {
    if (this.debug) {
        console.log(`[CircuitBreaker] processQueue: length ${this.queue.length}, requestInFlight ${this.requestInFlight}`)
    }
    // Don't process if queue is empty or request already in flight
    if (this.queue.length === 0 || this.requestInFlight) return;

    if (this.state === 'CLOSED') {
      // Circuit closed, execute head of queue
      this.requestInFlight = true;
      const request = this.queue[0];

      await this.applyDebugDelay(request);
      if (this.debug) {
        console.log(`[CircuitBreaker] processQueue: done waiting`)
      }

      // Proactively check if token needs refresh (skip for token-refresh requests)
      if (request.type !== 'token-refresh') {
        if (!request.needsTokenRefresh && tokenManager.needsRefresh()) {
          request.needsTokenRefresh = true;
        }

        // Handle token refresh if needed
        if (request.needsTokenRefresh) {
          const refreshed = await this.processTokenRefresh(request);
          if (!refreshed) return; // Auth error or backend still down
        }
      }

      if (request.type === 'http') {
        await this.processHTTPRequest(request);
      } else if (request.type === 'sse') {
        await this.processSSERequest(request);
      } else if (request.type === 'sse-connection') {
        await this.processSSEConnectionRequest(request);
      } else if (request.type === 'token-refresh') {
        await this.processTokenRefreshRequest(request);
      }
    } else if (this.state === 'OPEN' && !this.retryTimeout) {
      // Circuit open, schedule probe with backoff
      this.scheduleProbe();
    }
  }

  /**
   * Report a successful request
   */
  public reportSuccess(): void {
    if (this.debug) {
      console.log(`[CircuitBreaker] reportSuccess() called - current state: ${this.state}, consecutiveSuccesses: ${this.consecutiveSuccesses}`);
    }
    this.consecutiveFailures = 0;
    this.consecutiveSuccesses++;

    // Clear in-flight flag
    this.requestInFlight = false;

    // ALWAYS dequeue on success (whether OPEN or CLOSED)
    if (this.queue.length > 0) {
      const successfulRequest = this.queue.shift()!;
      // Resolve the promise if not already resolved (SSE case)
      if (successfulRequest.type === 'sse') {
        successfulRequest.resolve(undefined);
      }
    }

    if (this.state === 'OPEN') {
      // Probe succeeded, close circuit and flush remaining queue
      if (this.consecutiveSuccesses >= this.config.successThreshold) {
        this.closeCircuit();
      }
    } else if (this.state === 'CLOSED') {
      // Normal operation, reset retry count
      this.retryCount = 0;
      // Process next item in queue
      this.processQueue();
    }
  }

  /**
   * Report a canceled request (user-initiated cancellation)
   */
  public reportCanceled(): void {
    if (this.debug) {
      console.log(`[CircuitBreaker] reportCanceled() called - current state: ${this.state}`);
    }

    // Clear in-flight flag
    this.requestInFlight = false;

    // Dequeue the canceled request (won't be retried)
    if (this.queue.length > 0) {
      this.queue.shift();
    }

    // Don't update success/failure counters - this wasn't a backend issue

    // Process next item in queue (handles both CLOSED and OPEN states)
    this.processQueue();
  }

  /**
   * Report a failed request (network error or 5xx)
   */
  public reportFailure(error: any): void {
    // Clear in-flight flag
    this.requestInFlight = false;

    if (this.debug) {
      console.log(`[CircuitBreaker] reportFailure() called - state: ${this.state}, consecutiveFailures: ${this.consecutiveFailures}, queue.length: ${this.queue.length}, error:`, error?.message || error);
    }
    this.consecutiveSuccesses = 0;
    this.consecutiveFailures++;

    if (this.state === 'CLOSED') {
      // First failure, open circuit
      if (this.consecutiveFailures >= this.config.failureThreshold) {
        if (this.debug) {
          console.log('[CircuitBreaker] reportFailure() - calling openCircuit()');
        }
        this.openCircuit();
      }
    } else if (this.state === 'OPEN') {
      // Probe failed, schedule next probe if not already scheduled
      if (this.debug) {
        console.log(`[CircuitBreaker] reportFailure() - circuit is OPEN, retryTimeout: ${this.retryTimeout}, queue.length: ${this.queue.length}`);
      }
      if (!this.retryTimeout && this.queue.length > 0) {
        if (this.debug) {
          console.log('[CircuitBreaker] reportFailure() - calling scheduleProbe()');
        }
        this.scheduleProbe();
      } else {
        if (this.debug) {
          console.log('[CircuitBreaker] reportFailure() - NOT scheduling probe (retryTimeout:', this.retryTimeout, ', queue.length:', this.queue.length, ')');
        }
      }
    }
  }


  /**
   * Open the circuit (backend is down)
   */
  private openCircuit(): void {
    if (this.state === 'OPEN') return;

    if (this.debug) {
      console.log('[CircuitBreaker] Opening circuit - backend appears down');
      console.log(`[CircuitBreaker] openCircuit() - retryTimeout: ${this.retryTimeout}, queue.length: ${this.queue.length}`);
    }
    this.state = 'OPEN';
    this.consecutiveSuccesses = 0;

    this.notifyListeners();

    // Schedule first probe if we have queued requests
      if (!this.retryTimeout && this.queue.length > 0) {
      if (this.debug) {
        console.log('[CircuitBreaker] openCircuit() - calling scheduleProbe()');
      }
      this.scheduleProbe();
    } else {
      if (this.debug) {
        console.log('[CircuitBreaker] openCircuit() - NOT scheduling probe (retryTimeout:', this.retryTimeout, ', queue.length:', this.queue.length, ')');
      }
    }
  }

  /**
   * Close the circuit (backend is back up)
   */
  private closeCircuit(): void {
    if (this.state === 'CLOSED') return;

    if (this.debug) {
      console.log(`[CircuitBreaker] Closing circuit - backend recovered (queue size: ${this.queue.length})`);
    }
    this.state = 'CLOSED';
    this.retryCount = 0;
    this.consecutiveFailures = 0;
    this.consecutiveSuccesses = 0;
    this.nextRetryTime = null;

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    this.notifyListeners();

    this.flushQueue();
  }

  /**
   * Schedule a probe attempt with exponential backoff
   */
  private scheduleProbe(): void {
    // Calculate delay with exponential backoff and jitter
    const exponentialDelay = Math.min(this.config.baseDelay * Math.pow(2, this.retryCount), this.config.maxDelay);
    const jitter = exponentialDelay * this.config.jitterFactor * (Math.random() * 2 - 1);
    const delay = Math.max(exponentialDelay + jitter, this.config.baseDelay);

    // Store when the next retry will happen
    this.nextRetryTime = Date.now() + delay;

    if (this.debug) {
      console.log(`[CircuitBreaker] Scheduling probe in ${Math.round(delay)}ms (attempt ${this.retryCount + 1})`);
    }

    // Notify listeners with the next retry time
    this.notifyListeners();

    this.retryTimeout = setTimeout(() => {
      this.retryTimeout = null;
      this.nextRetryTime = null;
      this.executeProbe();
    }, delay);
  }

  /**
   * Execute a probe request to test if backend is back
   */
  private async executeProbe(): Promise<void> {
    if (this.queue.length === 0) {
      if (this.debug) {
        console.log('[CircuitBreaker] No queued requests to probe with');
      }
      return;
    }

    // Stay in OPEN state while probing
    this.retryCount++;

    if (this.debug) {
      console.log(`[CircuitBreaker] Executing probe (attempt ${this.retryCount})`);
    }

    // Notify listeners before probe so UI updates countdown
    this.notifyListeners();

    // Mark request in flight
    this.requestInFlight = true;

    // Peek at oldest request to use as probe (NEVER dequeue until success)
    const probeRequest = this.queue[0];

    // Proactively check if token needs refresh (skip for token-refresh requests)
    if (probeRequest.type !== 'token-refresh') {
      if (!probeRequest.needsTokenRefresh && tokenManager.needsRefresh()) {
        probeRequest.needsTokenRefresh = true;
      }

      // Handle token refresh if needed
      if (probeRequest.needsTokenRefresh) {
        const refreshed = await this.processTokenRefresh(probeRequest);
        if (!refreshed) return; // Auth error or backend still down
      }
    }

    if (probeRequest.type === 'http') {
      await this.processHTTPRequest(probeRequest);
    } else if (probeRequest.type === 'sse') {
      await this.processSSERequest(probeRequest);
    } else if (probeRequest.type === 'sse-connection') {
      await this.processSSEConnectionRequest(probeRequest);
    } else if (probeRequest.type === 'token-refresh') {
      await this.processTokenRefreshRequest(probeRequest);
    }
  }

  /**
   * Flush the queue with jittered delays
   */
  private flushQueue(): void {
    const queuedRequests = [...this.queue];
    this.queue = [];

    if (this.debug) {
      console.log(`[CircuitBreaker] Flushing ${queuedRequests.length} queued requests with jittered delays`);
    }

    queuedRequests.forEach((req, index) => {
      const jitter = Math.random() * this.config.releaseJitterMax;

      setTimeout(async () => {
        if (req.type === 'http') {
          // Check if this endpoint requires authentication
          const method = req.config.method?.toUpperCase() || 'GET';
          const url = req.config.url || '';
          const requiresAuth = !isPublicEndpoint(url, method);

          // Get current token and add to request if authentication required
          const token = requiresAuth ? tokenManager.getToken() : null;

          // Make axios call directly with bypass flag and token (if needed)
          const config = {
            ...req.config,
            __circuitBreakerBypass: true,
            headers: {
              ...req.config?.headers,
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          };

          try {
            const response = await AXIOS_INSTANCE(config);

            req.resolve(response.data);
          } catch (error: any) {
            // On flush, we don't retry - circuit is CLOSED
            // Just reject the promise and let caller handle it
            req.reject(error);
          }
        } else if (req.type === 'sse') {
          // Get current token and pass to SSE reconnect
          const token = tokenManager.getToken();
          if (!token) {
            req.reject(new Error('Authentication required'));
            return;
          }

          // Execute SSE reconnect with token
          try {
            await req.execute!(token);
            req.resolve(undefined);
          } catch (err) {
            req.reject(err);
          }
        }
      }, jitter);
    });
  }

  /**
   * Reject all queued requests with an error
   */
  private rejectAllQueued(error: Error): void {
    const queuedRequests = [...this.queue];
    this.queue = [];

    if (this.debug) {
      console.log(`[CircuitBreaker] Rejecting ${queuedRequests.length} queued requests`);
    }

    queuedRequests.forEach(req => {
      req.reject(error);
    });
  }

  /**
   * Notify all listeners of state change
   */
  private notifyListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener(this.state, this.retryCount, this.nextRetryTime || undefined);
      } catch (err) {
        console.error('[CircuitBreaker] Error in state change listener:', err);
      }
    });
  }

  /**
   * Reset the circuit breaker (for testing)
   */
  public reset(): void {
    this.state = 'CLOSED';
    this.retryCount = 0;
    this.consecutiveFailures = 0;
    this.consecutiveSuccesses = 0;
    this.requestInFlight = false;

    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }

    this.rejectAllQueued(new Error('Circuit breaker reset'));
    this.notifyListeners();
  }
}

// Global singleton instance
export const circuitBreaker = new CircuitBreaker();

// Expose to browser console for debugging
// Usage: window.circuitBreaker.debug = true
if (typeof window !== 'undefined') {
  (window as any).circuitBreaker = circuitBreaker;
}
